import TaskAltIcon from '@mui/icons-material/TaskAlt'
import {ArrowRight, BookOpen, Info, Landmark, Wand2} from 'lucide-react'
import React, {FC} from 'react'
import {useIntl} from 'react-intl'

import {toAbsoluteUrl} from '_metronic/helpers'

interface OnboardingWelcomeModalProps {
  onStartSetup: () => void
  onStartSampleData: () => void
  isLoading?: boolean
  showSampleDataOption?: boolean
  sampleDataCleared?: boolean
}

const OnboardingWelcomeModal: FC<OnboardingWelcomeModalProps> = ({
  onStartSetup,
  onStartSampleData,
  isLoading = false,
  showSampleDataOption = true,
  sampleDataCleared = false,
}) => {
  const intl = useIntl()
  const showSecondaryOption = showSampleDataOption && !sampleDataCleared
  const showActionDetails = !sampleDataCleared

  return (
    <div
      className='onboarding-welcome-overlay'
      role='dialog'
      aria-modal='true'
      aria-labelledby='welcome-modal-title'
    >
      <div className='onboarding-welcome-backdrop' />

      <div className='onboarding-welcome-content'>
        <div className='onboarding-welcome-card'>
          <div className='onboarding-welcome-card-inner'>
            <div className='onboarding-welcome-logo'>
              <img
                src={toAbsoluteUrl('media/auth/image 70.png')}
                alt=''
                className='onboarding-welcome-logo-img'
              />
              <span className='onboarding-welcome-logo-text'>TimeTable™</span>
            </div>

            <h1 id='welcome-modal-title' className='onboarding-welcome-heading'>
              {intl.formatMessage({id: 'ONBOARDING_WIZARD.WELCOME.HEADING'})}
            </h1>

            {sampleDataCleared && (
              <div className='onboarding-welcome-cleared-banner' role='status'>
                <div className='onboarding-welcome-cleared-banner__content'>
                  <span className='onboarding-welcome-cleared-banner__icon' aria-hidden>
                    <TaskAltIcon fontSize='inherit' />
                  </span>
                  <div>
                    <p className='onboarding-welcome-cleared-banner__title'>
                      {intl.formatMessage({
                        id: 'ONBOARDING_WIZARD.WELCOME.SAMPLE_DATA_CLEARED_TITLE',
                      })}
                    </p>
                    <p className='onboarding-welcome-cleared-banner__subtitle'>
                      {intl.formatMessage({
                        id: 'ONBOARDING_WIZARD.WELCOME.SAMPLE_DATA_CLEARED_SUBTITLE',
                      })}
                    </p>
                  </div>
                </div>
                <img
                  src={toAbsoluteUrl('green.jpg')}
                  alt=''
                  aria-hidden
                  className='onboarding-welcome-cleared-banner__art'
                />
              </div>
            )}

            {/* After a sample-data clear there is no "demo or real data" choice any more —
                sample mode can't be started again — so the intro says so instead of
                offering an option the user no longer has. */}
            <p className='onboarding-welcome-para'>
              {intl.formatMessage({
                id: sampleDataCleared
                  ? 'ONBOARDING_WIZARD.WELCOME.PARA1_AFTER_CLEAR'
                  : 'ONBOARDING_WIZARD.WELCOME.PARA1',
              })}
            </p>
            <p className='onboarding-welcome-para'>
              {intl.formatMessage({id: 'ONBOARDING_WIZARD.WELCOME.PARA2'})}
            </p>
          </div>
        </div>

        <div className='onboarding-welcome-actions' aria-busy={isLoading}>
          <div
            className={`onboarding-welcome-actions-row${
              showSecondaryOption ? '' : ' onboarding-welcome-actions-row--single'
            }`}
          >
            {showSecondaryOption && (
              <div className='onboarding-welcome-action-column'>
                <button
                  type='button'
                  className='onboarding-welcome-btn'
                  onClick={onStartSampleData}
                  disabled={isLoading}
                  aria-label={intl.formatMessage({
                    id: 'ONBOARDING_WIZARD.WELCOME.START_SAMPLE_DATA',
                  })}
                >
                  <BookOpen size={18} className='me-2' aria-hidden />
                  {intl.formatMessage({id: 'ONBOARDING_WIZARD.WELCOME.START_SAMPLE_DATA'})}
                  <ArrowRight size={18} className='ms-2' aria-hidden />
                </button>
                {showActionDetails ? (
                  <p className='onboarding-welcome-action-subtext'>
                    {intl.formatMessage({id: 'ONBOARDING_WIZARD.WELCOME.SAMPLE_DATA_SUBTEXT'})}
                  </p>
                ) : null}
              </div>
            )}

            <div className='onboarding-welcome-action-column'>
              <button
                type='button'
                className={`onboarding-welcome-btn${
                  showSecondaryOption ? ' onboarding-welcome-btn-secondary' : ''
                }`}
                onClick={onStartSetup}
                disabled={isLoading}
                aria-label={intl.formatMessage({id: 'ONBOARDING_WIZARD.WELCOME.START_SETUP'})}
              >
                {sampleDataCleared ? (
                  <Wand2 size={18} className='me-2' aria-hidden />
                ) : (
                  <Landmark size={18} className='me-2' aria-hidden />
                )}
                {intl.formatMessage({id: 'ONBOARDING_WIZARD.WELCOME.START_SETUP'})}
                {!sampleDataCleared ? <ArrowRight size={18} className='ms-2' aria-hidden /> : null}
              </button>
              {showActionDetails ? (
                <p className='onboarding-welcome-action-subtext'>
                  {intl.formatMessage({id: 'ONBOARDING_WIZARD.WELCOME.START_SETUP_SUBTEXT'})}
                </p>
              ) : null}
            </div>
          </div>

          {showSecondaryOption && showActionDetails ? (
            <div className='onboarding-welcome-sample-info'>
              <Info size={16} className='onboarding-welcome-sample-info-icon' aria-hidden />
              <span>{intl.formatMessage({id: 'ONBOARDING_WIZARD.WELCOME.SAMPLE_DATA_INFO'})}</span>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default OnboardingWelcomeModal
